import { useLocalStorageState } from '@side/pantry';
import { type ReactNode, useCallback, useMemo } from 'react';
import {
  useClearAgentsAndTeamsOnViewAsSwitch,
  useViewReportAs,
} from '../../helpers';
import type { PerformanceReportFiltersFormValues } from '../types';
import {
  PERFORMANCE_REPORT_CONTEXT_DEFAULTS,
  PerformanceReportContext,
  type PerformanceReportContextType,
} from './PerformanceReportContext';

interface Props {
  children: ReactNode;
  previousViewReportAs?: string;
}

const defaultFilters = PERFORMANCE_REPORT_CONTEXT_DEFAULTS.filters;

export const PerformanceReportProvider = ({
  children,
  previousViewReportAs,
}: Props) => {
  const viewReportAs = useViewReportAs();

  const [filters, setFilters] =
    useLocalStorageState<PerformanceReportFiltersFormValues>(
      `performanceReportFilters-${viewReportAs}`,
      defaultFilters,
    );

  useClearAgentsAndTeamsOnViewAsSwitch(
    previousViewReportAs,
    viewReportAs,
    setFilters,
  );

  const updateContext = useCallback(
    (newContext: Partial<PerformanceReportContextType>) => {
      if ('filters' in newContext) {
        setFilters(newContext.filters ?? defaultFilters);
      }
    },
    [setFilters],
  );

  const contextValue = useMemo(
    () => ({ filters, updateContext }),
    [filters, updateContext],
  );

  return (
    <PerformanceReportContext.Provider value={contextValue}>
      {children}
    </PerformanceReportContext.Provider>
  );
};
